import { JwtPayload } from "jwt-decode";
import { type RequestInternal, Session, User } from "next-auth";
import CredentialsProvider from "next-auth/providers/credentials";

import { authenticate, refreshToken } from "@/app/api/dynabuy-back/api-auth";
import { getBackAPIUrl } from "@/app/api/dynabuy-back/getBackAPIUrl";
import { authDiagEnabled, logAuthDiag, shortHash } from "@/utils/auth-diag";
import { revokeTokens } from "@/utils/auth-refresh";

// Étendre l'interface JwtPayload
export interface ExtendedJwtPayload extends Omit<JwtPayload, "error"> {
  id?: string;
  dynabuyToken?: string;
  dynabuyExpiry?: number;
  dynabuyRefreshToken?: string;
  firstName?: string;
  lastName?: string;
  avatar?: any;
  roles?: string[];
  email?: string | null;
  organization?: any;
  error?: "RefreshTokenError";
  [key: string]: unknown;
}

// Étendre l'interface User
interface ExtendedUser extends User {
  id: string;
  dynabuyToken?: string;
  dynabuyExpiry?: number;
  dynabuyRefreshToken?: string;
  firstName?: string;
  lastName?: string;
  avatar?: any;
  roles?: string[];
  organization?: any;
}

interface JwtUpdateSession {
  image?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export type RequestInternalType = Pick<
  RequestInternal,
  "body" | "query" | "headers" | "method"
>;

export const authConfig = {
  pages: {
    signIn: "/connexion",
  },
  providers: [
    CredentialsProvider({
      credentials: {
        username: { label: "Username", type: "text" },
        password: { label: "Password", type: "password" },
      },
      async authorize(
        credentials: Record<"username" | "password", string> | undefined,
        req: RequestInternalType,
      ) {
        return await authenticate(credentials, req);
      },
    }),
  ],
  events: {
    async signOut({ token }: { token: ExtendedJwtPayload }) {
      try {
        await revokeTokens((await getBackAPIUrl()) as string, token ?? {});
      } catch (error) {
        console.error("Logout API error:", error);
      }
    },
  },
  callbacks: {
    async jwt({
      token,
      user,
      trigger,
      session,
    }: {
      token: ExtendedJwtPayload;
      user: ExtendedUser | undefined;
      trigger?: "signIn" | "signUp" | "update";
      session?: JwtUpdateSession;
    }) {
      if (trigger === "update" && session?.image) {
        token.avatar.urls.avatar_md = session.image;
      }
      if (trigger === "update" && (session?.firstName || session?.lastName)) {
        token.firstName = session.firstName;
        token.lastName = session.lastName;
      }

      // Forcer le rafraîchissement du token si l'email a changé
      if (
        trigger === "update" &&
        session?.email &&
        session.email !== token.email
      ) {
        return await refreshToken(token);
      }

      if (user) {
        token = {
          ...token,
          id: user.id,
          dynabuyToken: user.dynabuyToken,
          dynabuyExpiry: user.dynabuyExpiry,
          dynabuyRefreshToken: user.dynabuyRefreshToken,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar,
          roles: user.roles,
          email: user.email,
          organization: user.organization,
        };
      }

      if (token.dynabuyExpiry && Date.now() < token.dynabuyExpiry) {
        // token is still valid
        return token;
      }

      // Token expiré : on ne rafraîchit pas ici (souvent en rendu RSC, où
      // l'écriture du cookie est no-op → boucle de refresh). Le refresh
      // proactif est fait par le middleware. Ici on signale l'erreur pour
      // déclencher un logout propre côté client.
      if (authDiagEnabled()) {
        logAuthDiag(
          `jwt refresh-trigger ` +
            `expiredByMs=${Date.now() - (token.dynabuyExpiry ?? 0)} ` +
            `priorError=${token.error ?? "-"} ` +
            `rtHash=${shortHash(token.dynabuyRefreshToken)} ` +
            `trigger=${trigger ?? "-"} ` +
            `hasUser=${!!user} ` +
            `tokenSize=${JSON.stringify(token).length}`,
        );
      }

      token.error = "RefreshTokenError";
      return token;
    },
    async session({
      session,
      token,
    }: {
      session: Session;
      token: ExtendedJwtPayload;
    }) {
      session.error = token.error;

      if (authDiagEnabled() && token.error) {
        logAuthDiag(
          `session-error error=${token.error} ` +
            `rtHash=${shortHash(token.dynabuyRefreshToken)}`,
        );
      }

      if (token) {
        session.user = {
          ...session.user,
          id: token.id,
          dynabuyToken: token.dynabuyToken,
          dynabuyRefreshToken: token.dynabuyRefreshToken,
          firstName: token.firstName,
          lastName: token.lastName,
          image: token.avatar ? token.avatar?.urls?.avatar_md : null,
          email: token.email,
          roles: token.roles,
          organization: token.organization,
        };
      }

      return session;
    },
  },
};
